import { PaymentPlan } from "./paymentPlan";

export const APPSTORE_VERIFY_URL = "https://buy.itunes.apple.com/verifyReceipt";
export const APPSTORE_VERIFY_SANDBOX_URL =
    "https://sandbox.itunes.apple.com/verifyReceipt";

export interface IosIapPlanProduct {
    productIdWithoutPrefix: string; // bundle_idを除く、アプリ内課金商品のproduct_id
    paymentPlanId?: PaymentPlan;
}

export const IOS_IAP_PLAN_PRODUCTS: IosIapPlanProduct[] = [
    {
        productIdWithoutPrefix: "subscription.standard.1month",
        paymentPlanId: PaymentPlan.Standard1MIosIap12000
    },
    {
        productIdWithoutPrefix: "subscription.lite_v2.1month",
        paymentPlanId: PaymentPlan.Lite1MIosIap1000
    }
];

export const AppleServerNotificationType = {
    CANCEL: "CANCEL",
    CONSUMPTION_REQUEST: "CONSUMPTION_REQUEST",
    DID_CHANGE_RENEWAL_PREF: "DID_CHANGE_RENEWAL_PREF",
    DID_CHANGE_RENEWAL_STATUS: "DID_CHANGE_RENEWAL_STATUS",
    DID_FAIL_TO_RENEW: "DID_FAIL_TO_RENEW",
    DID_RECOVER: "DID_RECOVER",
    DID_RENEW: "DID_RENEW",
    INITIAL_BUY: "INITIAL_BUY",
    INTERACTIVE_RENEWAL: "INTERACTIVE_RENEWAL",
    PRICE_INCREASE_CONSENT: "PRICE_INCREASE_CONSENT",
    REFUND: "REFUND",
    REVOKE: "REVOKE"
} as const;
export type AppleServerNotificationType = typeof AppleServerNotificationType[keyof typeof AppleServerNotificationType];

export interface AppleServerNotificationResponseBody {
    auto_renew_adam_id: string; // App Store Connectが生成し、AppStoreがユーザーのサブスクリプションが更新する自動更新可能なサブスクリプションを一意に識別するために使用する識別子。この値を64ビット整数として扱います。
    auto_renew_product_id: string; // ユーザーのサブスクリプションが更新する自動更新可能なサブスクリプションの製品ID。
    auto_renew_status: "true" | "false"; // 自動更新可能なサブスクリプション製品の現在の更新ステータス。これらの値は、領収書の値とは異なることに注意してください。
    auto_renew_status_change_date: string; // ISO 8601標準と同様の日時形式で、ユーザーが自動更新可能なサブスクリプションの更新ステータスをオンまたはオフにした時刻。
    auto_renew_status_change_date_ms: string; // ユーザーが自動更新可能なサブスクリプションの更新ステータスをオンまたはオフにした時刻（UNIXエポック時間形式）（ミリ秒単位）。この時間形式を使用して日付を処理します。
    auto_renew_status_change_date_pst: string; // 太平洋時間帯で、ユーザーが自動更新可能なサブスクリプションの更新ステータスをオンまたはオフにした時刻。
    environment: string; // AppStoreがレシートを生成した環境。
    expiration_intent: "1" | "2" | "3" | "4" | "5"; // サブスクリプションの有効期限が切れた理由。このフィールドは、期限切れの自動更新可能なサブスクリプションの場合にのみ表示されます。
    notification_type: AppleServerNotificationType; // 通知をトリガーしたサブスクリプションイベント。
    password: string; // レシートを検証するときにrequestBodyのpasswordフィールドに送信する共有シークレットと同じ値。
    unified_receipt: AppleUnifiedReceipt; // アプリの最新のアプリ内購入トランザクションに関する情報を含むオブジェクト。
    bid: string; // アプリバンドルIDを含む文字列。
    bvrs: string; // アプリバンドルのバージョンを含む文字列。
}

export interface AppleUnifiedReceipt {
    environment: "Sandbox" | "Production";
    latest_receipt: string; // base64
    latest_receipt_info: AppleLatestReceiptInfo[]; // 最新の100個のアプリ内購入トランザクションを含む配列
    pending_renewal_info: ApplePendingRenewalInfo[];
    status: "0";
}

export interface IosReceiptVerifyRequestBody {
    "receipt-data": string;
    password: string;
    "exclude-old-transactions"?: boolean;
}

export interface AppleReceiptVerifyResponseBody {
    status: AppleVerifyReceiptErrorCode;
    environment: "Sandbox" | "Production";
    receipt: AppleReceipt;
    "is-retryable": string;
    latest_receipt?: string; // base64
    latest_receipt_info?: AppleLatestReceiptInfo[];
    pending_renewal_info?: ApplePendingRenewalInfo[];
}

export interface AppleReceipt {
    adam_id: number;
    app_item_id: number;
    application_version: string;
    bundle_id: string;
    download_id: number;
    expiration_date?: string;
    expiration_date_ms?: string;
    expiration_date_pst?: string;
    in_app: AppleIAPTransaction[];
    original_application_version: string;
    original_purchase_date: string;
    original_purchase_date_ms: string;
    original_purchase_date_pst: string;
    preorder_date: string;
    preorder_date_ms: string;
    preorder_date_pst: string;
    receipt_creation_date_ms: string;
    receipt_creation_date_pst: string;
    receipt_type:
        | "Production"
        | "ProductionVPP"
        | "ProductionSandbox"
        | "ProductionVPPSandbox";
    request_date: string;
    request_date_ms: string;
    request_date_pst: string;
    version_external_identifier: number;
}

export interface AppleIAPTransaction {
    cancellation_date?: string; // Appleからの返金が来たときのみ値が入るのでoption
    cancellation_date_ms?: string; // Appleからの返金が来たときのみ値が入るのでoption
    cancellation_date_pst?: string; // Appleからの返金が来たときのみ値が入るのでoption
    cancellation_reason?: "0" | "1"; // Appleからの返金が来たときのみ値が入るのでoption
    expires_date: string;
    expires_date_ms: string;
    expires_date_pst: string;
    is_in_intro_offer_period: "true" | "false";
    is_trial_period: "true" | "false";
    original_purchase_date: string;
    original_purchase_date_ms: string;
    original_purchase_date_pst: string;
    original_transaction_id: string;
    product_id: string;
    promotional_offer_id: string;
    purchase_date: string;
    purchase_date_ms: string;
    purchase_date_pst: string;
    quantity: string;
    web_order_line_item_id: string;
    transaction_id: string;
}

export interface AppleLatestReceiptInfo extends AppleIAPTransaction {
    in_app_ownership_type: "FAMILY_SHARED" | "PURCHASED";
    is_upgraded?: string;
    offer_code_ref_name?: string; // NOTE: オファーコード未利用の場合、keyなしになる
    subscription_group_identifier: string;
}

export interface ApplePendingRenewalInfo {
    auto_renew_product_id?: string;
    auto_renew_status: "1" | "0";
    expiration_intent: "1" | "2" | "3" | "4" | "5";
    grace_period_expires_date: string;
    grace_period_expires_date_ms: string;
    grace_period_expires_date_pst: string;
    is_in_billing_retry_period: "1" | "0";
    offer_code_ref_name: string;
    original_transaction_id: string;
    price_consent_status: "1" | "0";
    product_id: string;
    promotional_offer_id: string;
}

export enum AppleVerifyReceiptErrorCode {
    NOT_POST = 21000,
    SHOULD_NOT_HAPPEN = 21001,
    INVALID_RECEIPT_OR_DOWN = 21002,
    UNAUTHORIZED = 21003,
    WRONG_SHARED_SECRET = 21004,
    SERVICE_DOWN = 21005,
    EXPIRED_SUBSCRIPTION_ONLY_IOS6 = 21006,
    USE_TEST_ENVIRONMENT = 21007,
    USE_PRODUCTION_ENVIRONMENT = 21008,
    APPLE_INTERNAL_ERROR = 21009,
    CUSTOMER_NOT_FOUND = 21010
}

export const IAPTransactionSourceType = {
    APPLE_WEBHOOK: "APPLE_WEBHOOK",
    RECEIPT_VERIFY_API: "RECEIPT_VERIFY_API",
    BATCH: "BATCH"
} as const;
export type IAPTransactionSourceType = typeof IAPTransactionSourceType[keyof typeof IAPTransactionSourceType];

export const IAPTransactionResultType = {
    STOP: "STOP", // isPaidをfalseにした場合
    START: "START", // isPaidをfalseからtrueにした場合
    RESTART: "RESTART", // isPaidをfalseからtrueにした場合(再購読:paymentTypeがREPEATで登録済)
    CHARGE: "CHARGE", // isPaidをtrueからtrueにした場合かつ、nextPaymentDateが新しくなったとき
    PAID_FINISH_DATE_UPDATE: "PAID_FINISH_DATE_UPDATE", // isPaidをtrueからtrueにした場合かつpaidFinishDateが新しくなったとき（終了予定になった）
    GRACE_PERIOD: "GRACE_PERIOD", // 猶予期間内だった場合かつ、gracePeriodDateが新しくなったとき
    NO_UPDATE: "NO_UPDATE", // DBへの変更はなし
    UNKNOWN: "UNKNOWN",
    IN_CARD_TRANSFER_APPLICATION: "IN_CARD_TRANSFER_APPLICATION", // カード移行申請中
    ANOTHER_USER_BILLING_ERROR: "ANOTHER_USER_BILLING_ERROR", // 他のユーザの決済の場合（originalTransactionIdが他のユーザに紐付いている場合）
    ERROR: "ERROR" // エラーが発生したとき
} as const;
export type IAPTransactionResultType = typeof IAPTransactionResultType[keyof typeof IAPTransactionResultType];

export enum IAPPaymentMethod {
    IOS_IAP = "ios_iap",
    ANDROID_IAP = "android_iap"
}
export enum OtherPaymentMethod {
    CREDIT_CARD = "credit_card"
}
export type PaymentMethod = IAPPaymentMethod | OtherPaymentMethod;

export enum IAPReceiptVerifyAPIStatus {
    SUCCESS = "SUCCESS",
    PURCHASE_EXPIRED = "PURCHASE_EXPIRED"
}
